import { FC, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Avatar,
  Box,
  ButtonBase,
  CardContent,
  Drawer,
  IconButton,
  Typography
} from '@material-ui/core'
import {
  removeKazooCallSession,
  removeKazooLiveNote,
  setSelectedNote,
  syncKazooData,
  updateKazooLiveNotes
} from 'src/slices/phone'
import EditIcon from '@material-ui/icons/EventNote'
import { formatPersonsPayload } from 'src/utils/communications'
import { getContactInitials } from 'src/utils/contacts'
import useAuth from '../../hooks/useAuth'
import { RootState } from '../../store'
import Scrollbar from '../Scrollbar'
import { sortListByDate } from '../../utils/common'
import Notes from './phone/Notes'

const NoteBar: FC = () => {
  const { user } = useAuth()
  const { auth0Token, accountId, australisId } = user
  const dispatch = useDispatch()
  const selectedNote = useSelector(
    (state: RootState) => state.phone.selectedNote
  )
  const contacts = useSelector((state: RootState) => state.contacts.contacts)
  const kazooUsers = useSelector((state: RootState) => state.phone.users)
  const liveNotes = useSelector((state: RootState) => state.phone.liveNotes)
  const formattedLiveNotes = sortListByDate(
    Object.keys(liveNotes).map((liveNote) => {
      const note = liveNotes[liveNote]
      if (typeof note.dateCreated === 'string') {
        return { ...note, dateCreated: parseInt(note.dateCreated, 10) }
      }
      return note
    }),
    'dateCreated',
    false
  )

  useEffect(() => {
    dispatch(syncKazooData(auth0Token, accountId, australisId))
  }, [])

  useEffect(() => {
    if (!selectedNote && Object.keys(liveNotes).length === 1) {
      const noteId = Object.keys(liveNotes)[0]
      dispatch(setSelectedNote(noteId))
    }
    if (!Object.keys(liveNotes).length) {
      dispatch(setSelectedNote(''))
    }
  }, [liveNotes])

  const handleSelectNote = (id: string) => {
    if (selectedNote === id) {
      dispatch(setSelectedNote(''))
    } else {
      dispatch(setSelectedNote(id))
    }
  }

  const handleChangeText = (
    interactionId: string,
    text: string,
    name: string
  ) => {
    if (interactionId === selectedNote && liveNotes[interactionId]) {
      const attr = name === 'body' ? 'note' : 'subject'
      const newNote = { ...liveNotes[interactionId], [attr]: text }
      dispatch(updateKazooLiveNotes(newNote))
    }
  }

  const handleChangeField = (
    interactionId: string,
    value: object[] | any,
    name: string
  ) => {
    if (interactionId === selectedNote) {
      const note = liveNotes[interactionId]
      const attr =
        name === 'From' ? 'sender' : name === 'To' ? 'receiver' : 'matterId'

      if (attr === 'matterId') {
        const matterValue = value ? value.matterId : ''
        const newNote = { ...note, [attr]: matterValue }
        dispatch(updateKazooLiveNotes(newNote))
        return
      }

      const personPayload = formatPersonsPayload(value, false, true)
      personPayload.forEach((p) => {
        const userId = p.id.replaceAll('-', '')
        if (!kazooUsers[userId]) return
        const callerIds = kazooUsers[userId]?.callerId
        p.phoneNumber =
          callerIds?.external?.number || callerIds?.internal?.number
      })

      const newNote = { ...note, [attr]: personPayload }
      dispatch(updateKazooLiveNotes(newNote))
    }
  }

  const handleCloseNote = (
    interactionId: string,
    isSession: boolean = false
  ) => {
    if (isSession) dispatch(removeKazooCallSession(auth0Token, interactionId))
    dispatch(removeKazooLiveNote(interactionId))
    if (formattedLiveNotes.length > 1) {
      const secondNote = formattedLiveNotes.find(
        (note) => note.interactionId !== interactionId
      )
      dispatch(setSelectedNote(secondNote.interactionId))
    } else {
      dispatch(setSelectedNote(''))
    }
  }

  // console.log('liveNotes in note bar', liveNotes)
  //   console.log('selectedNote in note bar', selectedNote)
  //   console.log('formattedLiveNotes', formattedLiveNotes)
  //   console.log('contacts', contacts)

  return (
    <>
      <Drawer
        anchor="right"
        open
        sx={{
          display: formattedLiveNotes.length > 1 ? 'block' : 'none'
        }}
        PaperProps={{
          sx: {
            backgroundColor: 'background.paper',
            height: 'calc(100% - 64px) !important',
            top: '64px !Important',
            right: !!selectedNote ? 370 : 0,
            transition: 'right 225ms cubic-bezier(0, 0, 0.2, 1) 0ms',
            alignItems: 'center',
            width: 110,
            overflowX: 'hidden'
          }
        }}
        variant="persistent"
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            height: '100%',
            overflow: 'hidden'
          }}
        >
          <IconButton
            disableRipple
            sx={{
              '&:hover': {
                background: 'none'
              },
              cursor: 'default'
            }}
          >
            <EditIcon sx={{ color: 'text.secondary' }} />
            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: '0.875rem',
                lineHeight: 2.5,
                fontWeight: 700,
                margin: '5px',
                padding: '5px'
              }}
            >
              Notes
            </Typography>
          </IconButton>

          <Scrollbar
            supressMobile
            options={{ suppressScrollX: true }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              paddingTop: 10,
              paddingRight: !!selectedNote ? 8 : 0,
              height: '100%'
            }}
          >
            {formattedLiveNotes.map((liveNote) => {
              const {
                interactionId,
                sender,
                receiver,
                dateCreated,
                direction
              } = liveNote

              let label
              if (direction === 'inbound') {
                label = getContactInitials(sender, australisId, contacts)
              } else {
                label = getContactInitials(receiver, australisId, contacts)
              }

              return (
                <ButtonBase
                  sx={{
                    padding: '10px 20px',
                    paddingTop: '15px',
                    paddingBottom: '15px',
                    marginLeft: !!selectedNote ? 0 : '5px',
                    width: '100%',
                    backgroundColor:
                      selectedNote === interactionId
                        ? 'rgba(210, 200, 243, 0.16)'
                        : 'none',
                    transition: 'background 0.5s, color 0.5s',
                    ':hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.08)',
                      color: 'white'
                    }
                  }}
                  key={`note-${interactionId}-${dateCreated}`}
                  onClick={() => handleSelectNote(interactionId)}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 50,
                        paddingRight: 5
                      }}
                    >
                      <Avatar
                        sx={{ width: 60, height: 60, fontSize: '2.2rem' }}
                      >
                        {label}
                      </Avatar>
                    </div>
                  </Box>
                </ButtonBase>
              )
            })}
          </Scrollbar>
        </Box>
      </Drawer>
      <Drawer
        anchor="right"
        open={!!selectedNote}
        PaperProps={{
          sx: {
            backgroundColor: 'background.paper',
            height: 'calc(100% - 64px) !important',
            top: '64px !Important',
            transition: 'right 225ms cubic-bezier(0, 0, 0.2, 1) 0ms',
            width: 380
          }
        }}
        variant="persistent"
      >
        <Scrollbar
          supressMobile
          style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        >
          {selectedNote && liveNotes[selectedNote] && (
            <CardContent>
              <Notes
                call={liveNotes[selectedNote]}
                closeNotes={handleCloseNote}
                onChangeText={handleChangeText}
                onChangeField={handleChangeField}
                type="kazoo"
              />
            </CardContent>
          )}
        </Scrollbar>
      </Drawer>
      )
    </>
  )
}

export default NoteBar
